import React, { useState } from "react";
import { IntlShape } from "react-intl";
import { toast } from "react-toastify";
import ConfirmModal from "../../../utils/modal/confirm";
import IntlMessages from "../../../utils/messages";
import { GroundGraphqlContextStore } from "../../../lib/ground-aws-graphql-core";
import FilterSelect from "../Filters/select";
import { EnumFilterFields, EnumFilterType } from "../../../utils/filter";
import { EnumQuoteStatus } from "../../../utils/quote";
import { Quote } from "../../../lib/ground-aws-graphql-core/models/Api/Quotes";
import Button from "../Button";
import history from "../../../history";
import { EnumPaths } from "../../../utils/navigation";
import { Center } from "../../../lib/ground-aws-graphql-core/models/Center";

interface Props {
  center: Center | null;
  quoteStatus: string[];
  quote: Quote;
  intl: IntlShape;
  className: string;
  fromQuoteDetails: boolean;
}

const DropdownQuoteStatus = ({
  center,
  quote,
  quoteStatus,
  intl,
  className,
  fromQuoteDetails,
}: Props): JSX.Element => {
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { id, ref, status, disabled, can_download_pdf, global_order } = quote;

  const currentStatus = {
    value: status,
    label: intl.messages[`page.list.quotes.status.${status}`] as string,
    disabled,
  };

  const [selectedStatus, setSelectedStatus] = useState<{
    value: string;
    label: string;
    disabled: boolean;
  }>(currentStatus);

  const updateQuote = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quote.updateQuote
  );

  const downloadPdf = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quote.downloadPdf
  );

  const confirmUpdateQuoteStatus = async () => {
    if (selectedStatus) {
      updateQuote({
        id,
        status: selectedStatus.value,
      })
        .then(() => {
          toast(
            intl.formatMessage({
              id: "page.quote.change.status.success",
            }),
            { type: "success" }
          );
          setIsModalOpen(false);
        })
        .catch(() => {
          toast(
            intl.formatMessage({
              id: "page.quote.change.status.error",
            }),
            { type: "error" }
          );
          setIsModalOpen(false);
        });
    }
  };

  const handleDownloadPdf = async (id: number, ref: string) => {
    setLoading(true);
    downloadPdf({
      id,
      number: ref,
    })
      .then(() => {
        toast(
          intl.formatMessage({
            id: "page.detail.quote.download.pdf.success",
          }),
          { type: "success" }
        );
      })
      .catch(() => {
        toast(
          intl.formatMessage({
            id: "page.detail.quote.download.pdf.error",
          }),
          { type: "error" }
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const field = {
    value: EnumFilterFields[EnumFilterFields.STATUS],
    type: EnumFilterType.SELECT,
    async: false,
    options: quoteStatus.map(o => ({
      label: intl.messages[`page.list.quotes.status.${o}`] as string,
      value: o,
      disabled: [
        EnumQuoteStatus.DRAFT,
        EnumQuoteStatus.ACCEPTED,
        EnumQuoteStatus.CANCELED,
        EnumQuoteStatus.EXPIRED,
      ].includes(EnumQuoteStatus[o]),
    })),
  };

  return (
    <>
      <div className={`flex flex-col ${className} w-full`} key={id}>
        <div id={`${id}`} data-cy="quote-status-selector" data-testid={id}>
          <FilterSelect
            selectedValue={currentStatus.value}
            defaultValue={false}
            disabled={currentStatus.disabled || loading}
            className="w-48"
            fields={[field]}
            onChange={(_type, target) => {
              setSelectedStatus(target);
              setIsModalOpen(!isModalOpen);
            }}
          />
        </div>

        {can_download_pdf && (
          <Button
            disabled={loading}
            id={`btn-download-pdf-${id}`}
            name="btn-download-pdf"
            item={quote}
            outline
            type="button"
            onClick={() => {
              handleDownloadPdf(id, ref);
            }}
            className="mt-2"
          >
            <IntlMessages id="page.detail.quote.download.pdf" />
          </Button>
        )}
        {global_order && center && fromQuoteDetails && (
          <div className="mt-2">
            <button
              className="mb-3 btn-link text-ground-blue-100 text-12px "
              type="button"
              onClick={e => {
                e.preventDefault();
                history.push({
                  pathname: `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${center.id}/${EnumPaths.GLOBAL_ORDERS}/${global_order.id}`,
                  state: { from: history.location.pathname },
                });
              }}
            >
              <IntlMessages id="page.list.bookings.show.order" />
            </button>
          </div>
        )}
      </div>
      <ConfirmModal
        isOpen={isModalOpen}
        toggle={() => {
          setIsModalOpen(!isModalOpen);
        }}
        onRequestClose={() => {
          setIsModalOpen(!isModalOpen);
        }}
        handleConfirm={confirmUpdateQuoteStatus}
        content={
          <IntlMessages
            id="page.quote.change.status.confirm"
            values={{
              ref: ref,
              status: selectedStatus?.label,
            }}
          />
        }
      />
    </>
  );
};

export default DropdownQuoteStatus;
